import {
  DataGridBox,
  ZebraDataGrid,
  CustomToolbar,
  ToolbarFilterRow,
  CustomNoRowsOverlay,
  GridFilterDateInput,
  ToolbarFilterDivider,
} from 'components';
import {
  DetailDrawer,
  CommentsListPopover,
  ProjectMonitoringCommentsCell,
  DeleteProjectReportCommentDialog,
} from './components';
import {
  Box,
  Chip,
  alpha,
  useTheme,
  TextField,
  Typography,
  Autocomplete,
  svgIconClasses,
  LinearProgress,
  linearProgressClasses,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  GridColDef,
  useGridApiRef,
  GridRowParams,
  GridColTypeDef,
  GRID_DATE_COL_DEF,
  GridActionsCellItem,
  GridRenderCellParams,
  getGridDateOperators,
} from '@mui/x-data-grid-premium';
import { Book } from 'pages/dashboard';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { PRIVATE_ROUTE_REGISTRY } from 'constants/routes';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProjectStepItem } from 'pages/project-step-managing/types';
import { ProcessedProjectReportItem } from './components/detail-drawer';
import { AddCommentPopover } from 'pages/project-step-managing/components';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { User, ProjectReportItem, ProjectReportComment, Project } from 'library/types';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { TABLE_SCHEMA_PAGE_NAME_ENUM, PROJECT_STEP_STATUS_TYPE_ENUM, PROJECT_STEP_PRIORITY_TYPE_ENUM } from 'enums';
import { Authentication, renderDataGridLocaleText, renderFormattedDate, renderAutoCompleteLocaleText } from 'utils';

const calculateWeightedProgress = (steps: ProjectStepItem[]): number => {
  if (steps.length === 0) return 0;

  let totalEffort = 0;
  let completedEffort = 0;
  let inProgressEffort = 0;

  const processStep = (step: {
    estimatedTaskDay: number;
    additionalEffortDay: number;
    status: PROJECT_STEP_STATUS_TYPE_ENUM;
  }) => {
    const effort = (step.estimatedTaskDay || 0) + (step.additionalEffortDay || 0);
    totalEffort += effort;

    if (step.status === PROJECT_STEP_STATUS_TYPE_ENUM.COMPLETED) {
      completedEffort += effort;
    } else if (step.status === PROJECT_STEP_STATUS_TYPE_ENUM.IN_PROGRESS) {
      inProgressEffort += effort * 0.5;
    }
  };

  for (const step of steps) {
    processStep(step);

    for (const subTask of step.subTasks ?? []) {
      processStep(subTask);
    }
  }

  if (totalEffort === 0) return 0;
  return Math.round(((completedEffort + inProgressEffort) / totalEffort) * 100);
};

const ProjectMonitoring = () => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const pageName = TABLE_SCHEMA_PAGE_NAME_ENUM.PROJECT_MONITORING;

  // redux hooks
  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState<Book[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [reports, setReports] = useState<ProjectReportItem[]>([]);
  const [projectSteps, setProjectSteps] = useState<ProjectStepItem[]>([]);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Project | null>(null);

  // Popover & Dialog states
  const [commentPopover, setCommentPopover] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    row: any | null;
    loading: boolean;
  }>({
    open: false,
    anchorEl: null,
    row: null,
    loading: false,
  });

  const [openDetail, setOpenDetail] = useState(false);
  const [detailRow, setDetailRow] = useState<ProcessedProjectReportItem | null>(null);

  const [viewAllPopover, setViewAllPopover] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    row: any | null;
  }>({
    open: false,
    anchorEl: null,
    row: null,
  });

  const [deleteCommentDialog, setDeleteCommentDialog] = useState<{
    open: boolean;
    comment: ProjectReportComment | null;
    row: any | null;
  }>({
    open: false,
    comment: null,
    row: null,
  });

  // async helper functions
  const getData = async () => {
    try {
      setLoading(true);
      const [reportsRes, booksRes, usersRes, stepsRes] = await Promise.all([
        apiService.getProjectReports(),
        apiService.getBooks(),
        apiService.getUsers(),
        apiService.getProjectSteps({ statusType: null, projectId: null, bookId: null, contactId: null }),
      ]);

      if (reportsRes.isSuccess) setReports(reportsRes.data.reports);
      if (booksRes.isSuccess) setBooks(booksRes.data);
      if (usersRes.isSuccess) setUsers(usersRes.data);
      if (stepsRes.isSuccess) setProjectSteps(stepsRes.data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getProjectStepStatusTypeTranslated = useCallback(
    (status: number) =>
      ({
        1: intl.formatMessage({ id: 'projectReport.status.notStarted', defaultMessage: 'Başlamadı' }),
        2: intl.formatMessage({ id: 'projectReport.status.inProgress', defaultMessage: 'Devam Ediyor' }),
        3: intl.formatMessage({ id: 'projectReport.status.completed', defaultMessage: 'Tamamlandı' }),
      })[status] ?? '-',
    [intl]
  );

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: 1, label: getProjectStepStatusTypeTranslated(1) },
      { value: 2, label: getProjectStepStatusTypeTranslated(2) },
      { value: 3, label: getProjectStepStatusTypeTranslated(3) },
    ],
    [getProjectStepStatusTypeTranslated]
  );

  const getPriorityTypeTranslated = useCallback(
    (priority: number) =>
      ({
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.nonUrgent',
          defaultMessage: 'Düşük',
        }),
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.urgent',
          defaultMessage: 'Orta',
        }),
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.veryUrgent',
          defaultMessage: 'Yüksek',
        }),
      })[priority] ?? '-',
    [intl]
  );

  const PRIORITY_OPTIONS = useMemo(
    () => [
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
        label: getPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT,
        label: getPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT,
        label: getPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT),
      },
    ],
    [getPriorityTypeTranslated]
  );

  const processedData = useMemo(() => {
    return reports
      .filter((report) => {
        if (selectedBook && report.bookId !== selectedBook.id) return false;
        if (selectedProduct && report.projectId !== selectedProduct.id) return false;
        return true;
      })
      .map((report) => {
        const relatedSteps = projectSteps.filter(
          (step) => step.bookId === report.bookId && step.projectId === report.projectId
        );

        if (relatedSteps.length === 0) return null;

        const targetBook = books.find((b) => b.id === report.bookId);
        const targetProductDetails = targetBook?.projects.find((p) => p.id === report.projectId);

        const allRequested = relatedSteps.every((s) => s.status === PROJECT_STEP_STATUS_TYPE_ENUM.REQUESTED);
        const allCompleted = relatedSteps.every((s) => s.status === PROJECT_STEP_STATUS_TYPE_ENUM.COMPLETED);

        let startDate: Date | null = null;
        let endDate: Date | null = null;

        if (relatedSteps.length > 0) {
          const sortedByDueDate = [...relatedSteps].sort(
            (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
          );
          const minStep = sortedByDueDate[0];
          const maxStep = sortedByDueDate[sortedByDueDate.length - 1];

          endDate = new Date(maxStep.dueDate);
          const effortDays = (minStep.estimatedTaskDay || 0) + (minStep.additionalEffortDay || 0);
          startDate = dayjs(minStep.dueDate).subtract(effortDays, 'day').toDate();
        }

        const statusValue = allRequested ? 1 : allCompleted ? 3 : 2;
        const progress = calculateWeightedProgress(relatedSteps);

        return {
          ...report,
          projectName: targetBook?.companyName || '-',
          productName: products.find((p) => p.id === report.projectId)?.title || '-',
          notes: targetProductDetails?.notes || '-',
          relatedContactId: targetProductDetails?.relatedContactId || null,
          alternativeRelatedContactId: targetProductDetails?.alternativeRelatedContactId || null,
          status: statusValue,
          priority: targetProductDetails?.priority || null,
          startDate,
          endDate,
          progress,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => {
        const projectDiff = a.projectName.localeCompare(b.projectName, intl.locale);
        if (projectDiff !== 0) return projectDiff;

        return a.productName.localeCompare(b.productName, intl.locale);
      });
  }, [reports, projectSteps, books, products, selectedBook, selectedProduct, intl.locale]);

  // Comment handlers
  const handleOpenAddComment = (row: any, anchorEl: HTMLElement) => {
    setCommentPopover({ open: true, anchorEl, row, loading: false });
  };

  const handleCloseAddComment = () => {
    setCommentPopover((prev) => ({ ...prev, open: false, anchorEl: null, row: null }));
  };

  const handleSubmitAddComment = async (commentText: string, row: any) => {
    try {
      setCommentPopover((prev) => ({ ...prev, loading: true }));

      const response = await apiService.addCommentToProjectReport(row.id, commentText);

      if (response.isSuccess) {
        setReports((prev) =>
          prev.map((r) => {
            if (r.id === row.id) {
              return {
                ...r,
                comments: [
                  ...r.comments,
                  {
                    id: response.data.id,
                    projectReportId: row.id,
                    commentText,
                    userId: users.find((u) => u.nameSurname === Authentication.getLoggedUser())?.id ?? 0,
                    createdAt: new Date().toISOString(),
                  },
                ],
              };
            }
            return r;
          })
        );
        handleCloseAddComment();
      }
    } catch {
    } finally {
      setCommentPopover((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleOpenViewAllComments = (row: any, anchorEl: HTMLElement) => {
    setViewAllPopover({ open: true, anchorEl, row });
  };

  const handleCloseViewAllComments = () => {
    setViewAllPopover((prev) => ({ ...prev, open: false, anchorEl: null, row: null }));
  };

  const handleOpenDeleteComment = (comment: ProjectReportComment, row: any) => {
    setDeleteCommentDialog({ open: true, comment, row });
  };

  const handleCloseDeleteComment = () => {
    setDeleteCommentDialog((prev) => ({ ...prev, open: false, comment: null, row: null }));
  };

  const handleConfirmDeleteComment = async () => {
    const { comment, row } = deleteCommentDialog;
    if (!comment || !row) return;

    try {
      const response = await apiService.deleteCommentFromProjectReport(comment.id);

      if (response.isSuccess) {
        enqueueSnackbar(intl.formatMessage({ id: 'projectStepManaging.deleteSuccessMessage' }), { variant: 'success' });
        setReports((prev) =>
          prev.map((r) => {
            if (r.id === row.id) {
              return {
                ...r,
                comments: r.comments.filter((c) => c.id !== comment.id),
              };
            }
            return r;
          })
        );
        handleCloseDeleteComment();
        handleCloseViewAllComments();
      }
    } catch {
      return;
    }
  };

  // Row detail handlers
  const openRowDetail = useCallback((row: ProcessedProjectReportItem) => {
    setDetailRow(row);
    setOpenDetail(true);
  }, []);

  const closeRowDetail = useCallback(() => {
    setOpenDetail(false);
    setDetailRow(null);
  }, []);

  const handleRowDoubleClick = useCallback(
    (params: GridRowParams<ProcessedProjectReportItem>, event: React.MouseEvent) => {
      if (event.defaultPrevented) return;
      const { bookId, projectId } = params.row;
      if (bookId == null || projectId == null) return;

      const targetBook = books.find((b) => b.id === bookId) || null;
      const targetProduct = products.find((p) => p.id === projectId) || null;

      navigate(PRIVATE_ROUTE_REGISTRY.projectStepManaging.path, {
        state: {
          bookId,
          projectId,
          book: targetBook,
          product: targetProduct,
        },
      });
    },
    [navigate, books, products]
  );

  const dateColumnType: GridColTypeDef<Date, string> = useMemo(
    () => ({
      ...GRID_DATE_COL_DEF,
      resizable: false,
      filterOperators: getGridDateOperators(false).map((item) => ({
        ...item,
        InputComponent: GridFilterDateInput,
        InputComponentProps: { showTime: false },
      })),
    }),
    []
  );

  const dateTimeColumnType: GridColTypeDef<Date, string> = useMemo(
    () => ({
      ...GRID_DATE_COL_DEF,
      resizable: false,
      filterOperators: getGridDateOperators(true).map((item) => ({
        ...item,
        InputComponent: GridFilterDateInput,
        InputComponentProps: { showTime: true },
      })),
    }),
    []
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'actions',
        type: 'actions',
        headerName: intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay' }),
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            key="detail"
            icon={<VisibilityOutlinedIcon sx={{ color: 'primary.main', opacity: 0.85 }} />}
            label={intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay' })}
            onClick={() => openRowDetail(params.row as ProcessedProjectReportItem)}
            showInMenu={false}
          />,
        ],
      },
      {
        field: 'bookId',
        headerName: intl.formatMessage({ id: 'projectReport.book', defaultMessage: 'Proje' }),
        minWidth: 200,
        flex: 1,
        type: 'singleSelect',
        valueOptions: () => books.filter((b) => !!b && b.id != null).map((b) => b.id),
        getOptionLabel: (value) => books.find((b) => String(b.id) === String(value))?.companyName || '',
        valueFormatter: (value) => books.find((b) => String(b.id) === String(value))?.companyName || '',
      },
      {
        field: 'projectId',
        headerName: intl.formatMessage({ id: 'projectReport.product', defaultMessage: 'Ürün' }),
        minWidth: 180,
        flex: 1,
        type: 'singleSelect',
        valueOptions: () => products.filter((p) => !!p && p.id != null).map((p) => p.id),
        getOptionLabel: (value) => products.find((p) => String(p.id) === String(value))?.title || '',
        valueFormatter: (value) => products.find((p) => String(p.id) === String(value))?.title || '',
      },
      {
        field: 'notes',
        headerName: intl.formatMessage({ id: 'projectReport.notes', defaultMessage: 'Notlar' }),
        minWidth: 250,
        flex: 1.5,
      },
      {
        field: 'relatedContactId',
        headerName: intl.formatMessage({ id: 'projectReport.relatedContact', defaultMessage: 'İlgili Kişi' }),
        minWidth: 180,
        type: 'singleSelect',
        valueOptions: () => users.map((u) => u.id),
        getOptionLabel: (value) => users.find((u) => u.id === value)?.nameSurname || '',
        valueFormatter: (value) => users.find((u) => u.id === value)?.nameSurname || '',
      },
      {
        field: 'alternativeRelatedContactId',
        headerName: intl.formatMessage({
          id: 'projectReport.alternativeRelatedContact',
          defaultMessage: 'Yedek İlgili Kişi',
        }),
        minWidth: 180,
        type: 'singleSelect',
        valueOptions: () => users.map((u) => u.id),
        getOptionLabel: (value) => users.find((u) => u.id === value)?.nameSurname || '',
        valueFormatter: (value) => users.find((u) => u.id === value)?.nameSurname || '',
      },
      {
        field: 'status',
        headerName: intl.formatMessage({ id: 'projectReport.status', defaultMessage: 'Statü' }),
        minWidth: 150,
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => STATUS_OPTIONS.map((x) => x.value),
        getOptionLabel: (value) => STATUS_OPTIONS.find((x) => x.value === value)?.label || String(value),
        valueFormatter: (value) => STATUS_OPTIONS.find((x) => x.value === value)?.label || '',
        renderCell: (params: GridRenderCellParams) => {
          const statusVal = params.value;
          const colorKey = statusVal === 1 ? 'info' : statusVal === 3 ? 'success' : 'secondary';

          return (
            <Chip
              size="small"
              label={getProjectStepStatusTypeTranslated(statusVal)}
              sx={{
                fontWeight: 700,
                bgcolor: alpha(theme.palette[colorKey].main, 0.1),
                color: theme.palette[colorKey].main,
                border: `1px solid ${alpha(theme.palette[colorKey].main, 0.3)}`,
                fontSize: '0.75rem',
                letterSpacing: '0.5px',
              }}
            />
          );
        },
      },
      {
        field: 'priority',
        headerName: intl.formatMessage({ id: 'projectReport.priority', defaultMessage: 'Öncelik' }),
        minWidth: 120,
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => PRIORITY_OPTIONS.map((x) => x.value),
        getOptionLabel: (value) => PRIORITY_OPTIONS.find((x) => x.value === value)?.label || String(value),
        valueFormatter: (value) => PRIORITY_OPTIONS.find((x) => x.value === value)?.label || '',
        renderCell: (params: GridRenderCellParams) => {
          if (!params.value) return '-';
          const priority = params.value;
          const colorKey =
            priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT
              ? 'success'
              : priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT
                ? 'warning'
                : 'error';

          return (
            <Chip
              size="small"
              label={getPriorityTypeTranslated(priority)}
              sx={{
                fontWeight: 600,
                bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                color: theme.palette[colorKey].main,
                border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
                cursor: 'default',
              }}
            />
          );
        },
      },
      {
        field: 'progress',
        headerName: intl.formatMessage({ id: 'projectReport.progress', defaultMessage: 'İlerleme' }),
        minWidth: 180,
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value ?? 0;
          const progressColor = theme.palette.success.main;

          return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={value}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 4,
                  [`&.${linearProgressClasses.colorPrimary}`]: {
                    bgcolor: alpha(progressColor, 0.15),
                  },
                  [`& .${linearProgressClasses.bar}`]: {
                    borderRadius: 4,
                    bgcolor: progressColor,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  minWidth: 38,
                  fontWeight: 700,
                  color: progressColor,
                  textAlign: 'right',
                }}>
                {value}%
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'startDate',
        ...dateColumnType,
        headerName: intl.formatMessage({ id: 'projectReport.startDate', defaultMessage: 'Başlangıç Tarihi' }),
        minWidth: 160,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value: any) => (value ? new Date(value) : null),
        valueFormatter: (value: any) => (value ? renderFormattedDate(value) : '-'),
      },
      {
        field: 'endDate',
        ...dateColumnType,
        headerName: intl.formatMessage({ id: 'projectReport.endDate', defaultMessage: 'Bitiş Tarihi' }),
        minWidth: 160,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value: any) => (value ? new Date(value) : null),
        valueFormatter: (value: any) => (value ? renderFormattedDate(value) : '-'),
      },
      {
        field: 'updatedAt',
        ...dateTimeColumnType,
        headerName: intl.formatMessage({ id: 'projectReport.updatedAt', defaultMessage: 'Son Güncellenme Tarihi' }),
        minWidth: 180,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value: any) => (value ? new Date(value) : null),
        valueFormatter: (value: any) => (value ? renderFormattedDate(value, true) : '-'),
      },
      {
        field: 'comments',
        headerName: intl.formatMessage({ id: 'projectReport.comments', defaultMessage: 'Yorumlar' }),
        minWidth: 350,
        flex: 1.5,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueFormatter: (value: ProjectReportComment[]) =>
          Array.isArray(value)
            ? value
                .map((comment) => {
                  if (!comment) return '';
                  const userName =
                    users.find((user) => user.id === comment.userId)?.nameSurname ?? 'Bilinmeyen Kullanıcı';
                  const text = comment.commentText ?? '';
                  return `${userName}: ${text}`;
                })
                .filter(Boolean)
                .join('\r\n')
            : '',
        renderCell: (params: GridRenderCellParams) => (
          <ProjectMonitoringCommentsCell
            row={params.row}
            comments={params.row.comments ?? []}
            currentUserId={users.find((u) => u.nameSurname === Authentication.getLoggedUser())?.id ?? 0}
            users={users}
            onAddComment={handleOpenAddComment}
            onViewAll={handleOpenViewAllComments}
          />
        ),
      },
    ],
    [
      intl,
      theme,
      users,
      books,
      products,
      STATUS_OPTIONS,
      PRIORITY_OPTIONS,
      getProjectStepStatusTypeTranslated,
      getPriorityTypeTranslated,
      dateColumnType,
      dateTimeColumnType,
      openRowDetail,
    ]
  );

  // useEffect hooks
  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
    getData();
  }, [dispatch, products.length, isEmptyProducts]);

  return (
    <DataGridBox sx={{ height: '80vh' }}>
      <ZebraDataGrid
        rows={processedData}
        columns={columns}
        loading={loading}
        pagination
        getRowId={(row) => row.id}
        localeText={renderDataGridLocaleText(intl)}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            exportPremiumExcel: true,
            excelStyleRules: [
              {
                field: 'comments',
                width: 50,
                wrapText: true,
                horizontal: 'left',
                vertical: 'top',
                getRowHeight: (value) => {
                  const lineCount = value ? value.split(/\r\n|\r|\n/).length : 1;
                  return 20 + (lineCount - 1) * 15;
                },
              },
              {
                field: 'status',
                width: 18,
                getCellStyle: (value) => {
                  if (value === 'Başlamadı') {
                    return { bg: 'E8F1FD', fg: '1E88E5', border: '90CAF9' };
                  }
                  if (value === 'Devam Ediyor') {
                    return { bg: 'F3E5F5', fg: '8E24AA', border: 'CE93D8' };
                  }
                  if (value === 'Tamamlandı') {
                    return { bg: 'E8F5E9', fg: '2E7D32', border: 'A5D6A7' };
                  }
                  return null;
                },
              },
              {
                field: 'priority',
                width: 16,
                getCellStyle: (value) => {
                  if (value === 'Düşük') {
                    return { bg: 'E8F5E9', fg: '2E7D32', border: 'A5D6A7' };
                  }
                  if (value === 'Orta') {
                    return { bg: 'FFF8E1', fg: 'F9A825', border: 'FFE082' };
                  }
                  if (value === 'Yüksek') {
                    return { bg: 'FFEBEE', fg: 'C62828', border: 'EF9A9A' };
                  }
                  return null;
                },
              },
              {
                field: 'progress',
                width: 14,
                getCellStyle: (value) => ({ bg: 'E8F5E9', fg: '2E7D32', border: 'A5D6A7' }),
              },
            ],
            showTemplates: true,
            apiRef,
            pageName,
            singleFilter: true,
            singleFilterComponent: (
              <ToolbarFilterRow>
                <Box>
                  <FormattedMessage id="projectReport.book" defaultMessage="Proje" />
                  &#58;&nbsp;
                </Box>
                <Autocomplete
                  size="small"
                  sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}
                  options={books}
                  value={selectedBook}
                  {...renderAutoCompleteLocaleText(intl)}
                  onChange={(_, newValue: Book | null) => setSelectedBook(newValue)}
                  getOptionLabel={(option: Book) => option.companyName ?? ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  slotProps={{
                    paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      sx={{
                        '& .MuiButtonBase-root': {
                          border: 'none',
                          backgroundColor: 'inherit',
                          width: '2.25rem',
                          height: '2.25rem',
                          padding: '0.25rem',
                          [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                          },
                        },
                      }}
                    />
                  )}
                />
                <ToolbarFilterDivider />
                <Box>
                  <FormattedMessage id="projectReport.product" defaultMessage="Ürün" />
                  &#58;&nbsp;
                </Box>
                <Autocomplete
                  size="small"
                  sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}
                  options={products}
                  value={selectedProduct}
                  {...renderAutoCompleteLocaleText(intl)}
                  onChange={(_, newValue: Project | null) => setSelectedProduct(newValue)}
                  getOptionLabel={(option: Project) => option.title ?? ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  slotProps={{
                    paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      sx={{
                        '& .MuiButtonBase-root': {
                          border: 'none',
                          backgroundColor: 'inherit',
                          width: '2.25rem',
                          height: '2.25rem',
                          padding: '0.25rem',
                          [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                          },
                        },
                      }}
                    />
                  )}
                />
              </ToolbarFilterRow>
            ),
          },
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: { variant: 'outlined', size: 'small' },
              columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
              operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
              valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
            },
          },
        }}
        disableRowSelectionOnClick
        onRowDoubleClick={handleRowDoubleClick}
        apiRef={apiRef}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />

      <AddCommentPopover
        open={commentPopover.open}
        anchorEl={commentPopover.anchorEl}
        row={commentPopover.row}
        loading={commentPopover.loading}
        onClose={handleCloseAddComment}
        onSubmit={handleSubmitAddComment}
      />

      <CommentsListPopover
        open={viewAllPopover.open}
        anchorEl={viewAllPopover.anchorEl}
        comments={viewAllPopover.row?.comments ?? []}
        row={viewAllPopover.row}
        users={users}
        currentUserId={users.find((u) => u.nameSurname === Authentication.getLoggedUser())?.id ?? 0}
        onClose={handleCloseViewAllComments}
        onDeleteComment={handleOpenDeleteComment}
      />

      <DeleteProjectReportCommentDialog
        open={deleteCommentDialog.open}
        onClose={handleCloseDeleteComment}
        onConfirm={handleConfirmDeleteComment}
      />

      <DetailDrawer
        open={openDetail}
        close={closeRowDetail}
        detailRow={detailRow}
        books={books}
        products={products}
        users={users}
        getProjectStepStatusTypeTranslated={getProjectStepStatusTypeTranslated}
        getPriorityTypeTranslated={getPriorityTypeTranslated}
      />
    </DataGridBox>
  );
};

export default ProjectMonitoring;
